export type EvoAirportsToggleResult = {
  nextSelected: boolean;
  primary?: AirportFilter[];
  secondary?: AirportFilter[];
};

export function unionAirportsByIata(primary?: AirportFilter[], secondary?: AirportFilter[]): AirportFilter[] {
  const result: AirportFilter[] = [];
  const indexByIata = new Map<string, number>();
  [...(primary || []), ...(secondary || [])].forEach((airport) => {
    const existingIndex = indexByIata.get(airport.iata);
    if (existingIndex === undefined) {
      indexByIata.set(airport.iata, result.length);
      result.push({ ...airport });
      return;
    }
    const existing = result[existingIndex];
    result[existingIndex] = {
      ...existing,
      selected: existing.selected || airport.selected,
      enabled: existing.enabled || airport.enabled,
    };
  });
  return result;
}

export function isIataSelected(iata: string, primary?: AirportFilter[], secondary?: AirportFilter[]): boolean {
  return [...(primary || []), ...(secondary || [])].some((airport) => airport.iata === iata && airport.selected);
}

export function toggleIataSelection(
  iata: string,
  primary?: AirportFilter[],
  secondary?: AirportFilter[]
): EvoAirportsToggleResult {
  const nextSelected = !isIataSelected(iata, primary, secondary);
  const applyToList = (list?: AirportFilter[]) =>
    list?.map((airport) => (airport.iata === iata ? { ...airport, selected: nextSelected } : airport));
  return {
    nextSelected,
    primary: applyToList(primary),
    secondary: applyToList(secondary),
  };
}

export function shouldShowSameAirportsToggle(
  outboundDepartureAirports?: AirportFilter[],
  inboundDepartureAirports?: AirportFilter[]
): boolean {
  return (outboundDepartureAirports?.length || 0) > 1 || (inboundDepartureAirports?.length || 0) > 1;
}

export type StationKind = 'airport' | 'train';

export type AirportGroup = {
  key: string;
  city?: string;
  kind: StationKind;
  airports: AirportFilter[];
};

// Only explicit station types decide the kind; an option without data (or with another type) follows the search mode.
export function getStationKind(info: AirportDistance | undefined, defaultKind: StationKind): StationKind {
  if (info?.stationType === 'RAILWAY') return 'train';
  if (info?.stationType === 'AIRPORT') return 'airport';
  return defaultKind;
}

export function buildAirportInfoByIata(distances?: AirportDistance[]): Map<string, AirportDistance> {
  return new Map((distances || []).map((distance) => [distance.iata, distance]));
}

// Without the hotel position the distance is unknown (distanceInMeters is undefined), not zero
export function hasHotelDistance(iata: string, infoByIata: Map<string, AirportDistance>): boolean {
  return infoByIata.get(iata)?.distanceInMeters !== undefined;
}

export function hasMixedStationTypes(
  iatas: string[],
  infoByIata: Map<string, AirportDistance>,
  defaultKind: StationKind
): boolean {
  const kinds = new Set(iatas.map((iata) => getStationKind(infoByIata.get(iata), defaultKind)));
  return kinds.has('airport') && kinds.has('train');
}

export function sortAirportsByDistance(
  airports: AirportFilter[],
  infoByIata: Map<string, AirportDistance>
): AirportFilter[] {
  return [...airports].sort((a, b) => {
    const distanceA = infoByIata.get(a.iata)?.distanceInMeters;
    const distanceB = infoByIata.get(b.iata)?.distanceInMeters;
    if (distanceA !== undefined && distanceB !== undefined && distanceA !== distanceB) return distanceA - distanceB;
    if ((distanceA === undefined) !== (distanceB === undefined)) return distanceA === undefined ? 1 : -1;
    return a.name.localeCompare(b.name) || a.iata.localeCompare(b.iata);
  });
}

export function groupAirportsByCityAndType(
  airports: AirportFilter[],
  infoByIata: Map<string, AirportDistance>,
  defaultKind: StationKind
): AirportGroup[] {
  const groups = new Map<string, AirportGroup>();
  airports.forEach((airport) => {
    const info = infoByIata.get(airport.iata);
    const kind = getStationKind(info, defaultKind);
    // An option whose city is unknown stays out of any city group instead of borrowing the selected flight's city
    const city = info?.cityName || undefined;
    const key = `${city ?? ''}|${kind}`;
    const group = groups.get(key);
    if (group) {
      group.airports.push(airport);
    } else {
      groups.set(key, { key, city, kind, airports: [airport] });
    }
  });

  const nearest = (group: AirportGroup): number | undefined => {
    const distances = group.airports
      .map((airport) => infoByIata.get(airport.iata)?.distanceInMeters)
      .filter((distance): distance is number => distance !== undefined);
    return distances.length > 0 ? Math.min(...distances) : undefined;
  };

  return [...groups.values()]
    .map((group) => ({ group, nearest: nearest(group) }))
    .sort((a, b) => {
      if (a.nearest !== b.nearest) {
        if (a.nearest === undefined) return 1;
        if (b.nearest === undefined) return -1;
        return a.nearest - b.nearest;
      }
      const cityOrder = (a.group.city ?? '').localeCompare(b.group.city ?? '');
      if (cityOrder !== 0) return cityOrder;
      if (a.group.kind !== b.group.kind) return a.group.kind === 'airport' ? -1 : 1;
      return 0;
    })
    .map(({ group }) => ({ ...group, airports: sortAirportsByDistance(group.airports, infoByIata) }));
}
